import { useStore } from '../../store/useStore';
import { FiX, FiMinus, FiPlus, FiCheck } from 'react-icons/fi';
import type { FontFamily, Theme } from '../../types';

const SettingsPanel = () => {
  const {
    readerSettings,
    setFontSize,
    setLineHeight,
    setFontFamily,
    setTheme,
    
    updateReaderSettings,
    toggleSettings,
  } = useStore();

  const fontOptions: { value: FontFamily; label: string }[] = [
    { value: 'nanum-myeongjo', label: '나눔명조' },
    { value: 'nanum-gothic', label: '나눔고딕' },
    { value: 'gothic', label: '고딕' },
    { value: 'serif-kr', label: '세리프' },
    { value: 'system', label: '시스템' },
  ];

  // 🎨 12가지 테마 옵션 (라이트 6개 + 다크 6개)
  const themeOptions: { value: Theme; label: string; emoji: string; desc: string; preview: { bg: string; text: string } }[] = [
    // 라이트 테마
    { value: 'light', label: '화이트', emoji: '☀️', desc: '밝은 환경', preview: { bg: 'bg-white', text: 'text-gray-900' } },
    { value: 'sepia', label: '세피아', emoji: '📜', desc: '클래식', preview: { bg: 'bg-[#f4ecd8]', text: 'text-[#5c4a3a]' } },
    { value: 'green', label: '그린', emoji: '🌿', desc: '눈 보호', preview: { bg: 'bg-green-50', text: 'text-green-900' } },
    { value: 'blue', label: '블루', emoji: '🌊', desc: '집중', preview: { bg: 'bg-blue-50', text: 'text-blue-900' } },
    { value: 'rose', label: '로즈', emoji: '🌸', desc: '따뜻함', preview: { bg: 'bg-pink-50', text: 'text-pink-900' } },
    { value: 'gray', label: '그레이', emoji: '🌫️', desc: '중립', preview: { bg: 'bg-gray-100', text: 'text-gray-800' } },
    // 다크 테마
    { value: 'dark', label: '다크', emoji: '🌙', desc: '어두운 환경', preview: { bg: 'bg-gray-900', text: 'text-gray-100' } },
    { value: 'black', label: '블랙', emoji: '⚫', desc: 'AMOLED', preview: { bg: 'bg-black', text: 'text-white' } },
    { value: 'midnight', label: '미드나잇', emoji: '🌌', desc: '깊은 밤', preview: { bg: 'bg-[#0f172a]', text: 'text-blue-100' } },
    { value: 'forest', label: '포레스트', emoji: '🌲', desc: '숲속 밤', preview: { bg: 'bg-[#0d1f0d]', text: 'text-green-100' } },
    { value: 'wine', label: '와인', emoji: '🍷', desc: '우아함', preview: { bg: 'bg-[#1a0a0a]', text: 'text-rose-100' } },
    { value: 'ocean', label: '오션', emoji: '🐋', desc: '심해', preview: { bg: 'bg-[#0a1628]', text: 'text-cyan-100' } },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 w-80 h-full shadow-xl p-6 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          독서 설정
        </h2>
        <button
          onClick={toggleSettings}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          <FiX size={20} />
        </button>
      </div>

      {/* Font Family */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          폰트
        </label>
        <div className="grid grid-cols-2 gap-2">
          {fontOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setFontFamily(option.value)}
              className={`px-4 py-2 rounded-lg border transition-colors ${
                readerSettings.fontFamily === option.value
                  ? 'border-primary bg-primary text-white'
                  : 'border-gray-300 dark:border-gray-600 hover:border-primary'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Font Size */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          글자 크기
        </label>
        <div className="flex items-center justify-between">
          <button
            onClick={() => setFontSize(Math.max(10, readerSettings.fontSize - 2))}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            disabled={readerSettings.fontSize <= 10}
          >
            <FiMinus size={20} />
          </button>

          <div className="flex-1 mx-4">
            <input
              type="range"
              min="10"
              max="32"
              step="2"
              value={readerSettings.fontSize}
              onChange={(e) => setFontSize(Number(e.target.value))}
              className="w-full"
            />
            <div className="text-center text-sm text-gray-600 dark:text-gray-400 mt-1">
              {readerSettings.fontSize}pt
            </div>
          </div>

          <button
            onClick={() => setFontSize(Math.min(32, readerSettings.fontSize + 2))}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            disabled={readerSettings.fontSize >= 32}
          >
            <FiPlus size={20} />
          </button>
        </div>
      </div>

      {/* Line Height */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          줄간격
        </label>
        <div className="flex items-center justify-between">
          <button
            onClick={() =>
              setLineHeight(Math.max(1.0, Number((readerSettings.lineHeight - 0.2).toFixed(1))))
            }
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            disabled={readerSettings.lineHeight <= 1.0}
          >
            <FiMinus size={20} />
          </button>

          <div className="flex-1 mx-4">
            <input
              type="range"
              min="1.0"
              max="2.5"
              step="0.1"
              value={readerSettings.lineHeight}
              onChange={(e) => setLineHeight(Number(e.target.value))}
              className="w-full"
            />
            <div className="text-center text-sm text-gray-600 dark:text-gray-400 mt-1">
              {readerSettings.lineHeight.toFixed(1)}
            </div>
          </div>

          <button
            onClick={() =>
              setLineHeight(Math.min(2.5, Number((readerSettings.lineHeight + 0.2).toFixed(1))))
            }
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            disabled={readerSettings.lineHeight >= 2.5}
          >
            <FiPlus size={20} />
          </button>
        </div>
      </div>

      {/* Theme - 라이트 모드 */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          ☀️ 라이트 모드
        </label>
        <div className="grid grid-cols-3 gap-2">
          {themeOptions.slice(0, 6).map((option) => (
            <button
              key={option.value}
              onClick={() => setTheme(option.value)}
              className={`relative p-2 rounded-lg border-2 transition-all ${
                readerSettings.theme === option.value
                  ? 'border-primary ring-2 ring-primary ring-offset-1'
                  : 'border-gray-200 dark:border-gray-600 hover:border-primary'
              }`}
              title={option.desc}
            >
              {/* 미리보기 */}
              <div className={`w-full h-8 rounded ${option.preview.bg} ${option.preview.text} flex items-center justify-center mb-1 text-xs font-medium border border-gray-300`}>
                가
              </div>
              <div className="text-center">
                <span className="text-sm">{option.emoji}</span>
                <span className="block text-xs text-gray-600 dark:text-gray-400">{option.label}</span>
              </div>
              {readerSettings.theme === option.value && (
                <FiCheck className="absolute top-1 right-1 text-primary" size={14} />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Theme - 다크 모드 */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          🌙 다크 모드
        </label>
        <div className="grid grid-cols-3 gap-2">
          {themeOptions.slice(6).map((option) => (
            <button
              key={option.value}
              onClick={() => setTheme(option.value)}
              className={`relative p-2 rounded-lg border-2 transition-all ${
                readerSettings.theme === option.value
                  ? 'border-primary ring-2 ring-primary ring-offset-1'
                  : 'border-gray-200 dark:border-gray-600 hover:border-primary'
              }`}
              title={option.desc}
            >
              {/* 미리보기 */}
              <div className={`w-full h-8 rounded ${option.preview.bg} ${option.preview.text} flex items-center justify-center mb-1 text-xs font-medium border border-gray-600`}>
                가
              </div>
              <div className="text-center">
                <span className="text-sm">{option.emoji}</span>
                <span className="block text-xs text-gray-600 dark:text-gray-400">{option.label}</span>
              </div>
              {readerSettings.theme === option.value && (
                <FiCheck className="absolute top-1 right-1 text-primary" size={14} />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Preserve Original Style */}
      <div className="mb-6">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={readerSettings.preserveOriginalStyle}
            onChange={(e) =>
              updateReaderSettings({ preserveOriginalStyle: e.target.checked })
            }
            className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="text-gray-700 dark:text-gray-300">
            원본 스타일 유지
          </span>
        </label>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-8">
          출판사가 지정한 원본 스타일을 사용합니다
        </p>
      </div>

      {/* Auto-scroll */}
      <div className="mb-6">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={readerSettings.autoScroll}
            onChange={(e) =>
              updateReaderSettings({ autoScroll: e.target.checked })
            }
            className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="text-gray-700 dark:text-gray-300">
            자동 스크롤
          </span>
        </label>
      </div>

      {/* Reset Button */}
      <button
        onClick={() => {
          setFontSize(16);
          setLineHeight(1.6);
          setFontFamily('nanum-gothic');
          setTheme('light');
          updateReaderSettings({ preserveOriginalStyle: false, autoScroll: false });
        }}
        className="w-full py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        기본값으로 초기화
      </button>
    </div>
  );
};

export default SettingsPanel;
